/**
 * A "store account" is the Supabase user the `shopify-oauth-callback` edge
 * function creates for a Shopify install: a synthetic
 * `store_<shop>_<6hex>@mora-ghost.io` email, a random password, and
 * `app_metadata: { is_ghost: true, shopify_domain }` (app_metadata is
 * server-only-writable). That synthetic address is an implementation detail and
 * must never be rendered or used as a name — show the shop instead.
 *
 * Every surface that renders a user's email or email-derived name goes through
 * these helpers so the rule lives in one place.
 */

const GHOST_EMAIL_SUFFIX = '@mora-ghost.io';

export interface StoreAccountUserLike {
  email?: string | null;
  // Record, not a named shape: Supabase's UserAppMetadata shares no declared
  // key with one, which trips TypeScript's weak-type check.
  app_metadata?: Record<string, unknown> | null;
}

export function isStoreAccount(user: StoreAccountUserLike | null | undefined): boolean {
  if (!user) return false;
  if (user.app_metadata?.is_ghost === true) return true;
  return typeof user.email === 'string' && user.email.toLowerCase().endsWith(GHOST_EMAIL_SUFFIX);
}

export function storeAccountShopDomain(
  user: StoreAccountUserLike | null | undefined,
): string | null {
  const domain = user?.app_metadata?.shopify_domain;
  return typeof domain === 'string' && domain.trim() ? domain.trim() : null;
}

/**
 * What to show wherever a normal user's email would be rendered, or null for a
 * regular user (caller renders the email).
 */
export function storeAccountIdentityLabel(
  user: StoreAccountUserLike | null | undefined,
): string | null {
  if (!isStoreAccount(user)) return null;
  const shop = storeAccountShopDomain(user);
  return shop ? `Shopify store · ${shop}` : 'Shopify store';
}

/**
 * Display label for an account-roster profile (`{ fullName, email }`): the
 * full name, else `storeLabel` for a store account (never its synthetic
 * email), else the email, else `fallback`.
 */
export function profileDisplayLabel(
  profile: { fullName?: string | null; email?: string | null } | null | undefined,
  fallback: string,
  storeLabel = 'the store account',
): string {
  const fullName = profile?.fullName?.trim();
  if (fullName) return fullName;
  if (isStoreAccount({ email: profile?.email })) return storeLabel;
  return profile?.email || fallback;
}
